import styles from '@cp/pages/Paramount/Documentation/styles.module.scss';
import React from 'react';

function Playground() {
  return (
    <div className={styles.documentationContainer}>
      <h1>Understanding the Key Concepts and DSL Fields</h1>

      <h2>Timeline</h2>
      <p>
        <strong>Definition</strong>: A grouping mechanism for strategies based on a common anchor
        date.
      </p>
      <p>
        <strong>Purpose</strong>:
      </p>
      <ul>
        <li>Created for tenants products of specific types.</li>
        <li>One timeline for one or more products of a tenant.</li>
        <li>Tenants can create multiple timelines at the product level.</li>
      </ul>

      <h2>Segment</h2>
      <p>
        <strong>Definition</strong>: A grouping mechanism for cases based on predefined criteria.
      </p>
      <p>
        <strong>Purpose</strong>:
      </p>
      <ul>
        <li>Allows the grouping of cases with similar attributes.</li>
        <li>
          Helps in creating reusable grouping definitions that can be used across multiple
          strategies.
        </li>
        <li>Defines conditions for strategy application.</li>
      </ul>
      <p>
        <strong>Example</strong>:
      </p>
      <pre>
        <code>
          {`segment "UNPAID_MP_EMI_MT_5K" {
    description = "Unpaid all mandate failed customers"
    engine = "COLLECTION_CDC"
    criteria = "{"eventName":"collection_cases_v2",
    "fieldConstraints":[
      {"value":
        {"data":160,"type":"NUMBER"},
        "operator":"GTE",
        "fieldName":"effortAttributes.PL_LENDING|totalOverdueAmount"},
        {"value":{"data":30,"type":"NUMBER"},
        "operator":"LT",
        "fieldName":"effortAttributes.PL_LENDING|emiAmount"}],
        "scriptConstraints":[
          {
            "params":{"var":"id","result":7,"operand":10,"operator":"GT"},
            "scriptName":"COMPARE_FIELD_WITH_MOD_RESULT"}
        ],
        "crossEventsJoinFieldName":"id"}"
  }`}
        </code>
      </pre>
      <p>
        <strong>Segment Field Explanation:</strong>
      </p>
      <ul>
        <li>
          <strong>description</strong>: Human-readable description of the Segment.
        </li>
        <li>
          <strong>engine</strong>: Segmentation engine to be used. Currently, only COLLECTION_CDC is
          supported.
        </li>
        <li>
          <strong>criteria</strong>: Segment evaluation criteria as per the selected segmentation
          engine.
        </li>
      </ul>

      <h2>Strategy</h2>
      <p>
        <strong>Definition</strong>: Defines scheduled actions to be taken on cases meeting
        specified conditions.
      </p>
      <p>
        <strong>Purpose</strong>:
      </p>
      <ul>
        <li>Associated with a timeline.</li>
        <li>Timelines can have multiple strategies.</li>
        <li>Strategies are anchored on the anchor date of the timeline.</li>
        <li>Specifies conditions, pre-actions, and actions for execution.</li>
      </ul>
      <p>
        <strong>Creating a Strategy</strong>:
      </p>
      <pre>
        <code>
          {`strategy "PresentMandateStrategy" {
  timeline = "PLCollectionsTimeline"
  description = "Present Mandate for emi &gt; 5000 cases"
  condition_type = "SEGMENT_BASED"
  condition = "{
    "setExpressions":{
      "final_result":{
        "entityOne":{
          "name":"UNPAID_ALL_MAN_FAILED",
          "entityType":"SUB_SEGMENT"},
          "operation":"MINUS",
          "entityTwo":{
            "name":"UNPAID_MP_EMI_MT_5K",
            "entityType":"SUB_SEGMENT"
  }}}}"
  dpad = [30..60, 90, 120]
  intent = "mandate"
  init_time = [11:47]
  pre_action = {}
  actions = {
  PRESENT_MANDATE {
  type = "static"
  amount_percentage = "100"
  balance_source = "SPADE"
}
}`}
        </code>
      </pre>
      <p>
        <strong>Strategy Field Explanation:</strong>
      </p>
      <ul>
        <li>
          <strong>timeline</strong>: Timeline name of which this strategy is part.
        </li>
        <li>
          <strong>description</strong>: Human-readable description of the Strategy.
        </li>
        <li>
          <strong>condition_type</strong>: Defines the condition parsing engine to use for
          evaluating the segment. Currently supports CDC_SEGMENT_BASED.
        </li>
        <li>
          <strong>condition</strong>: Defines the condition on the segment on which the Strategy has
          to be applied.
        </li>
        <li>
          <strong>dpad</strong>: DPAD stands for days past anchor date. It specifies the range of
          days past the anchor date for applying the strategy. It can be specified as a range using
          or individual days separated by commas.
        </li>
        <li>
          <strong>intent</strong>: Free text specifying the intent for creating this strategy.
        </li>
        <li>
          <strong>init_time</strong>: Initiation time of the strategy. If the same strategy has to
          be run multiple times in a day, pass a list of times in HH:MM format.
        </li>
        <li>
          <strong>pre_action</strong>: Actions to perform before the actual action. Can be used for
          setup or cleanup tasks required before the main action.
        </li>
        <li>
          <strong>actions</strong>: Contains actions to execute as part of this strategy. All
          executions will be carried out in the specified order; if any action fails, subsequent
          actions will not be executed.
        </li>
      </ul>

      <h2>Trigger</h2>
      <p>
        <strong>Definition</strong>: Defines instant actions to be taken on cases as soon as they
        meet a certain condition.
      </p>
      <p>
        <strong>Purpose</strong>:
      </p>
      <ul>
        <li>Associated with a timeline.</li>
        <li>Timelines can have multiple triggers.</li>
        <li>Triggers are anchored on the anchor date of the timeline.</li>
        <li>Specifies conditions, pre-actions, and actions for execution.</li>
      </ul>
      <p>
        <strong>Creating a Trigger</strong>:
      </p>
      <pre>
        <code>
          {`trigger "DuePaidTrigger" {
  timeline = "PLCollectionsTimeline"
  description = "Sample trigger description"
  condition_type = "EVENT_BASED"
  condition = "DuePaidEvent"
  actions = {
  SEND_COMMUNICATION {
  time = "09:30"
  communication_type = "SMS"
}
}}`}
        </code>
      </pre>
      <p>
        <strong>Trigger Field Explanation:</strong>
      </p>
      <ul>
        <li>
          <strong>timeline</strong>: Timeline name of which this trigger is part.
        </li>
        <li>
          <strong>description</strong>: Human-readable description of the Trigger.
        </li>
        <li>
          <strong>condition_type</strong>: Defines the condition parsing engine to use for
          evaluating the trigger. Currently supports EVENT_BASED.
        </li>
        <li>
          <strong>condition</strong>: Defines the condition on the trigger on which the Trigger has
          to be applied.
        </li>
        <li>
          <strong>actions</strong>: Contains actions to execute as part of this trigger. All
          executions will be carried out in the specified order; if any action fails, subsequent
          actions will not be executed.
        </li>
      </ul>
    </div>
  );
}

export default Playground;
